import { format } from "date-fns";
import { Check } from "lucide-react";
import { useEffect, useRef, type CSSProperties } from "react";
import type { Identifier } from "ra-core";
import type { Task } from "../types";
import { calendarTaskStyle } from "../deals/projectColors";
import { taskPeriodLabel } from "./taskPeriod";

import { taskCompleted } from "./calendarModel";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export function TaskPeriodBands({
  bands,
  personalIds,
  days,
  colors,
  onOpen,
  canMove,
}: {
  bands: { task: Task; track: number }[];
  personalIds: Set<string>;
  days: Date[];
  colors: Map<string, string>;
  onOpen: (id: Identifier) => void;
  canMove: (task: Task) => boolean;
}) {
  const layer = useRef<HTMLDivElement>(null);
  const highlight = (id: Identifier | null) => {
    layer.current
      ?.closest(".crm-month-grid")
      ?.querySelectorAll<HTMLElement>(".crm-period-band")
      .forEach((el) => {
        el.dataset.linked = String(
          id != null && el.dataset.taskId === String(id),
        );
      });
  };
  useEffect(() => {
    const el = layer.current;
    if (!el) return;
    const measure = () =>
      el
        .closest<HTMLElement>(".crm-month-week")
        ?.style.setProperty(
          "--task-content-height",
          `${el.getBoundingClientRect().height}px`,
        );
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    measure();
    return () => observer.disconnect();
  }, []);
  const first = format(days[0], "yyyy-MM-dd"),
    last = format(days[6], "yyyy-MM-dd");
  return (
    <div ref={layer} className="crm-period-layer">
      {bands.map(({ task, track }) => {
        const begin = task.installation_start! >= first,
          end = task.installation_end! <= last;
        const col = begin
          ? days.findIndex(
              (d) => format(d, "yyyy-MM-dd") === task.installation_start!,
            )
          : 0;
        const finish = end
          ? days.findIndex(
              (d) => format(d, "yyyy-MM-dd") === task.installation_end!,
            )
          : 6;
        return (
          <Tooltip key={task.id}>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="crm-task-chip crm-period-band"
                draggable={canMove(task)}
                onDragStart={(e) => {
                  e.stopPropagation();
                  e.dataTransfer.setData("text/task-id", String(task.id));
                }}
                data-start={begin}
                data-end={end}
                data-task-id={String(task.id)}
                onPointerEnter={() => highlight(task.id)}
                onPointerLeave={() => highlight(null)}
                onFocus={() => highlight(task.id)}
                onBlur={() => highlight(null)}
                style={
                  {
                    ...calendarTaskStyle(
                      task,
                      colors.get(String(task.deal_id)),
                    ),
                    gridColumn: `${col + 1} / span ${finish - col + 1}`,
                    gridRow: track + 1,
                  } as CSSProperties
                }
                aria-label={`${task.text}: ${taskPeriodLabel(task)}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpen(task.id);
                }}
              >
                {begin && (
                  <>
                    {begin && (
                      <>
                        <span>
                          {format(new Date(task.scheduled_start!), "HH:mm")}
                        </span>{" "}
                      </>
                    )}
                    {taskCompleted(task) && (
                      <Check
                        className="size-3 inline-block mr-1"
                        aria-label="Выполнено"
                      />
                    )}
                    {personalIds.has(String(task.id)) && (
                      <strong className="crm-personal-badge mr-1">
                        Личная
                      </strong>
                    )}
                  </>
                )}
                <span className="crm-task-title">{task.text}</span>
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-72">
              <strong className="block">{task.text}</strong>
              <span className="block">{taskPeriodLabel(task)}</span>
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}
